import React, { useMemo } from 'react';
import { PropertyDocument } from 'types/Documents';
import { FileType } from 'types/Files';
import DownloadIcon from 'assets/icons/download.svg?react';
import FileTypeIcon from 'components/Icons/FileTypeIcon';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import Loader from 'components/Loader/Loader';
import styles from './PropertyDocuments.module.css';
import { formatDateMonthYear } from 'utils/date';

type DocumentListProps = {
    documents: PropertyDocument[];
    isLoading: boolean;
};

const IMAGE_TYPES = new Set<FileType>([FileType.PNG, FileType.JPG, FileType.JPEG]);

export default function DocumentList({ documents, isLoading }: DocumentListProps) {
    const { imageFiles, otherFiles } = useMemo(() => {
        const imageFiles = [];
        const otherFiles = [];
        for (const doc of documents) {
            if (IMAGE_TYPES.has(doc.file_type)) imageFiles.push(doc);
            else otherFiles.push(doc);
        }
        return { imageFiles, otherFiles };
    }, [documents]);

    if (isLoading) {
        return <Loader centered size="sm" />;
    }

    if (documents.length === 0) {
        return <EmptyWidget title="No documents yet" message="Documents you upload will appear here." />;
    }

    return (
        <div className={styles.listContainer}>
            {imageFiles.length > 0 && (
                <div className={styles.imageGrid}>
                    {imageFiles.map((doc) => (
                        <div key={doc.id} className={styles.imageThumbnail}>
                            <img src={doc.signed_url} alt={doc.display_name} />
                            <a
                                href={doc.signed_url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className={`${styles.imageDownloadButton} ${styles.downloadIcon}`}
                                aria-label={`Download ${doc.display_name}`}
                            >
                                <DownloadIcon />
                            </a>
                        </div>
                    ))}
                </div>
            )}
            {otherFiles.length > 0 && (
                <div className={styles.fileList}>
                    {otherFiles.map((doc) => (
                        <div key={doc.id} className={styles.fileItem}>
                            <FileTypeIcon fileType={doc.file_type} size={20} />
                            <span className={styles.fileName}>
                                {doc.display_name}.{doc.file_type}
                            </span>
                            <span className={styles.fileDate}>
                                Uploaded on {formatDateMonthYear(new Date(Number(doc.created_at) * 1000))}
                            </span>
                            <a
                                href={doc.signed_url}
                                download
                                target="_blank"
                                rel="noreferrer"
                                className={styles.downloadIcon}
                                aria-label={`Download ${doc.display_name}`}
                            >
                                <DownloadIcon />
                            </a>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
